import type { AdminDashboardStats } from '@inflip/contracts/admin';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { ErrorBanner, PageHeader } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';

interface QueueCard {
  label: string;
  value: number;
  to: string;
  search?: Record<string, string | boolean>;
  urgent?: boolean;
}

export function DashboardPage() {
  const { session } = useAdminAuth();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .dashboardStats(session.accessToken)
      .then((s) => !cancelled && setStats(s))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!session) return null;

  const queues: QueueCard[] = stats
    ? [
        {
          label: '입금 확인 대기',
          value: stats.paymentPendingCount,
          to: '/orders',
          // 주문 목록은 기본이 최근 7일 — 대기 건은 기간 없이 모두 보여야 숫자가 맞는다
          search: { status: 'PAYMENT_PENDING', from: '', to: '' },
        },
        {
          // 추가금은 주문 상태가 결제 완료 그대로라 입금 확인 대기에 섞이지 않는다
          label: '추가금 입금 대기',
          value: stats.addonPaymentPendingCount,
          to: '/orders',
          search: { status: 'ADDON_PENDING', from: '', to: '' },
        },
        {
          // 입금 확인 뒤 결제 설정의 기준 일수가 지나도록 발급 전 — 판매자에게는 매일 채팅으로 안내
          label: '발급 지연',
          value: stats.licenseIssueDelayedCount,
          to: '/orders',
          search: { status: 'PAID', delayed: true, from: '', to: '' },
          urgent: true,
        },
        { label: '리스팅 심사 대기', value: stats.reviewPendingCount, to: '/listings' },
        {
          label: '오퍼 심사 대기',
          value: stats.offerReviewPendingCount,
          to: '/listings',
          search: { filter: 'OFFER_REVIEW' },
        },
        {
          label: '분쟁 처리 중',
          value: stats.disputedCount,
          to: '/orders',
          search: { status: 'DISPUTED', from: '', to: '' },
          urgent: true,
        },
        {
          // 분쟁은 끝났는데 운영자가 [나가기]를 하지 않은 방 — 당사자 화면에 운영자가 계속 보인다
          label: '분쟁 끝난 방 미퇴장',
          value: stats.adminStayedChatRoomCount,
          to: '/chats',
          search: { adminStayed: true },
        },
        { label: '환불 처리 대기', value: stats.refundRequestedCount, to: '/refunds' },
        {
          // 판매자가 정산금을 돌려주지 않아 접속이 정지됨 — 구매자 환불도 멈춰 있어 직접 연락이 필요
          label: '회수 기한 지남',
          value: stats.payoutClawbackOverdueCount,
          to: '/refunds',
          search: { status: '', clawback: 'OVERDUE' },
          urgent: true,
        },
        {
          label: '입금 반환 대기',
          value: stats.depositReturnPendingCount,
          to: '/deposit-returns',
        },
        { label: '정산 대기', value: stats.settlementWaitingCount, to: '/settlements' },
        {
          // 회원이 남긴 1:1 문의 — 분쟁·위반 신고 중인 당사자의 연락 창구이기도 하다
          label: '1:1 문의 답변 대기',
          value: stats.supportWaitingCount,
          to: '/support',
          urgent: true,
        },
      ]
    : [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader title="대시보드" description="오늘 처리할 일과 핵심 지표를 모아뒀어요" />
      {error && <ErrorBanner message={error} />}

      <h2 className="mb-3 text-base font-extrabold tracking-tight">처리 대기</h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6 xl:grid-cols-12">
        {queues.map((q) => {
          const danger = q.urgent && q.value > 0;
          return (
            <Link
              key={q.label}
              to={q.to}
              search={q.search}
              className={`rounded-lg px-4 py-3.5 transition hover:opacity-80 ${
                danger ? 'bg-rose-50' : 'bg-muted/60'
              }`}
            >
              <p
                className={`text-xs font-semibold ${danger ? 'text-rose-600' : 'text-muted-foreground'}`}
              >
                {q.label}
              </p>
              <p
                className={`mt-1 text-3xl font-extrabold tabular-nums tracking-tight ${danger ? 'text-rose-600' : ''}`}
              >
                {q.value}
              </p>
            </Link>
          );
        })}
      </div>

      {stats && (
        <>
          <h2 className="mb-3 mt-10 text-base font-extrabold tracking-tight">지표</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric label="오늘 주문" value={`${stats.todayOrderCount}건`} />
            <Metric
              label="오늘 입금 확인액"
              value={`${stats.todayConfirmedAmount.toLocaleString('ko-KR')}원`}
            />
            <Metric label="전체 회원" value={`${stats.totalUserCount}명`} />
            <Metric label="판매 중 IP" value={`${stats.publishedListingCount}개`} />
          </div>
        </>
      )}
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/60 px-4 py-3.5">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 truncate text-2xl font-extrabold tabular-nums tracking-tight">{value}</p>
    </div>
  );
}
